import { ChangeEvent, InputHTMLAttributes, RefObject, useEffect } from "react";
import { SearchIcon, XIcon } from "lucide-react";
import { useDebounce } from "use-debounce";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

type ClearableSearchInputProps = {
  containerClassName?: string;
  clearButtonClassName?: string;
  disabled?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  value: string;
  trackingScreenName?: MIXPANEL_SCREENS;
  trackingMetadata?: Record<string, unknown>;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "disabled" | "onChange" | "placeholder" | "value"
>;

export const ClearableSearchInput = ({
  clearButtonClassName,
  containerClassName,
  disabled,
  inputRef,
  onChange,
  onClear,
  placeholder,
  value,
  trackingScreenName,
  trackingMetadata,
  ...inputProps
}: ClearableSearchInputProps) => {
  const { className, ...restInputProps } = inputProps;

  // Fires the debounced search-tracking event when a screen name is provided.
  const [debouncedValue] = useDebounce(value, 400);
  useEffect(() => {
    if (!trackingScreenName) return;
    if (debouncedValue === "") return;
    mixpanelUtils.trackFilterAndSortOptionSelected(
      trackingScreenName,
      "Search",
      debouncedValue,
      trackingMetadata,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleClear = () => {
    mixpanelUtils.trackButtonClick(
      "Clear search",
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      trackingScreenName || getScreenFromPathname(location.pathname),
    );
    onChange("");
    onClear?.();
    inputRef?.current?.focus();
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-slate-500" />
      <Input
        ref={inputRef}
        className={cn("w-full px-9", className)}
        disabled={disabled}
        onChange={handleChange}
        placeholder={placeholder}
        value={value}
        {...restInputProps}
      />
      {value && (
        <Button
          className={cn(
            "absolute right-3 top-1/2 h-auto w-auto -translate-y-1/2 transform hover:bg-transparent",
            clearButtonClassName,
          )}
          onClick={handleClear}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon className="h-4 w-4 text-slate-500" />
        </Button>
      )}
    </div>
  );
};
